import React from 'react';
import { Link } from 'react-router-dom';
import { modules, guides, maneuvers, magazineIssues, featuredIssue } from '../mock';
import { ArrowRight, ArrowUpRight, BookOpen, Clock } from 'lucide-react';
import { FadeUp, Stagger, StaggerItem } from './MotionReveals';

export function ModulesSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-12 sm:mt-20">
      <FadeUp>
        <div className="flex items-end justify-between mb-5 sm:mb-8 gap-4">
          <div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Explorer</div>
            <h2 className="font-serif-editorial text-white text-[26px] sm:text-[36px] lg:text-[52px] leading-tight sm:leading-none tracking-tight">Découvrir nos modules</h2>
          </div>
          <Link to="/tutos" className="hidden md:inline-flex items-center gap-1.5 text-white/80 hover:text-white text-[13.5px] font-medium">Tout voir <ArrowUpRight size={15} /></Link>
        </div>
      </FadeUp>
      <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3.5" staggerChildren={0.06}>
        {modules.map((m, i) => (
          <StaggerItem key={i}>
            <Link to="/tutos" className="group relative rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 bg-[#111113] aspect-[4/5] card-lift block">
              <img src={m.img} alt={m.title} className="absolute inset-0 w-full h-full object-cover img-zoom opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
              {m.tag && (
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 px-1.5 sm:px-2 py-0.5 rounded-md bg-red-600 text-white text-[9px] sm:text-[10px] font-bold tracking-wider">
                  {m.tag}
                </div>
              )}
              <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4">
                <h3 className="font-serif-editorial text-white text-[15px] sm:text-[22px] leading-tight">{m.title}</h3>
                <p className="text-white/60 text-[10.5px] sm:text-[12px] mt-1 font-mono-spec">{m.meta}</p>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

export function GuidesSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
      <div className="flex items-end justify-between mb-5 sm:mb-8 gap-4">
        <div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Magazine</div>
          <h2 className="font-serif-editorial text-white text-[26px] sm:text-[36px] lg:text-[52px] leading-tight sm:leading-none tracking-tight">Guides premium</h2>
        </div>
        <Link to="/magazine" className="hidden md:inline-flex items-center gap-1.5 text-white/80 hover:text-white text-[13.5px] font-medium">Tous les guides <ArrowUpRight size={15} /></Link>
      </div>
      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Big featured */}
        {guides.slice(0, 2).map((g, i) => (
          <Link key={i} to="/magazine" className="group relative rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 bg-[#111113] aspect-[16/11] card-lift">
            <img src={g.img} alt={g.title} className="absolute inset-0 w-full h-full object-cover img-zoom" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
            {g.tag && (
              <div className="absolute top-3 left-3 sm:top-4 sm:left-4 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold tracking-wider">{g.tag}</div>
            )}
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-5 sm:left-5 sm:right-5">
              {g.subtitle && <div className="text-red-400 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-semibold mb-1.5 sm:mb-2">{g.subtitle}</div>}
              <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] lg:text-[28px] leading-tight">{g.title}</h3>
              <p className="text-white/60 text-[11.5px] sm:text-[12px] mt-1.5 sm:mt-2 font-mono-spec">{g.meta}</p>
            </div>
          </Link>
        ))}
        {/* Themes block */}
        <Link to="/magazine" className="group relative rounded-xl sm:rounded-2xl overflow-hidden border border-red-900/40 bg-gradient-to-br from-red-950/40 via-[#150a0a] to-[#0d0d0e] aspect-[16/11] flex flex-col justify-between p-5 sm:p-6 card-lift">
          <div>
            <div className="text-red-400 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-semibold mb-2">Programme officiel · 9 thèmes</div>
            <h3 className="font-serif-editorial text-white text-[22px] sm:text-[28px] leading-tight">Apprendre par thème</h3>
            <p className="text-white/60 text-[12px] sm:text-[13px] mt-1.5 sm:mt-2">Magazine pédagogique · 9 dossiers ETG</p>
          </div>
          <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
            {magazineIssues.slice(0, 9).map((m, idx) => (
              <div key={idx} className="rounded-md bg-white/5 border border-white/10 px-1.5 sm:px-2 py-1 sm:py-1.5 text-[9.5px] sm:text-[10px] text-white/80 font-mono-spec text-center">
                {m.n}
              </div>
            ))}
          </div>
        </Link>
      </div>
      {/* Small cards */}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
        {guides.slice(3, 6).map((g, i) => (
          <Link key={i} to="/magazine" className="group relative rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 bg-[#111113] aspect-[16/11] card-lift">
            <img src={g.img} alt={g.title} className="absolute inset-0 w-full h-full object-cover img-zoom" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-5 sm:left-5 sm:right-5">
              {g.subtitle && <div className="text-red-400 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-semibold mb-1.5 sm:mb-2">{g.subtitle}</div>}
              <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px] leading-tight">{g.title}</h3>
              <p className="text-white/60 text-[11.5px] sm:text-[12px] mt-1.5 sm:mt-2 font-mono-spec">{g.meta}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function ManeuversSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
      <FadeUp>
        <div className="flex items-end justify-between mb-5 sm:mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Épreuve pratique · Officiel 2026</div>
            <h2 className="font-serif-editorial text-white text-[26px] sm:text-[36px] lg:text-[52px] leading-[1.02] sm:leading-[0.95] tracking-tight max-w-3xl">Les 6 manœuvres en un coup d’œil</h2>
            <p className="mt-3 sm:mt-4 text-white/60 text-[13.5px] sm:text-[15px] max-w-2xl">L’épreuve unique imposée sur tous les centres d’examen français. 32 minutes pour démontrer ta maîtrise.</p>
          </div>
          <Link to="/tutos" className="hidden md:inline-flex items-center gap-1.5 text-white/80 hover:text-white text-[13.5px] font-medium">Parcours complet <ArrowUpRight size={15} /></Link>
        </div>
      </FadeUp>
      <Stagger className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3" staggerChildren={0.07}>
        {maneuvers.map((m, i) => (
          <StaggerItem key={i} className="group relative rounded-xl border border-white/10 bg-gradient-to-br from-[#141416] to-[#0c0c0d] p-4 sm:p-5 hover:border-red-700/40 transition-colors hover:-translate-y-1">
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <span className="font-mono-spec text-[30px] sm:text-[42px] lg:text-[56px] leading-none text-red-500/80 group-hover:text-red-500 transition-colors">{m.n}</span>
              <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{m.label}</span>
            </div>
            <h3 className="font-serif-editorial text-white text-[16px] sm:text-[22px] leading-tight">{m.title}</h3>
            <p className="text-white/60 text-[12px] sm:text-[13px] mt-1">{m.sub}</p>
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/5 text-white/40 text-[10.5px] sm:text-[11.5px] font-mono-spec">{m.meta}</div>
          </StaggerItem>
        ))}
      </Stagger>
      <div className="mt-6 sm:mt-8 p-4 sm:p-5 rounded-xl border border-white/10 bg-[#0d0d0e] flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-red-400 font-semibold">Schéma officiel · Vidéos tutos · Notation A/B/C</div>
          <div className="text-white text-[13.5px] sm:text-[15px] mt-1">Tout le parcours épreuve pratique, gratuit et sans inscription.</div>
        </div>
        <div className="flex gap-2 sm:gap-3 flex-wrap">
          <button className="h-10 px-4 rounded-full bg-white text-black text-[12.5px] sm:text-[13.5px] font-semibold hover:bg-white/90">Découvrir le parcours</button>
          <button className="h-10 px-4 rounded-full border border-white/15 text-white text-[12.5px] sm:text-[13.5px] font-medium hover:bg-white/5">Voir tout</button>
        </div>
      </div>
    </section>
  );
}

export function MagazineSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
      <div className="mb-5 sm:mb-8">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Programme pédagogique</div>
        <h2 className="font-serif-editorial text-white text-[26px] sm:text-[36px] lg:text-[52px] leading-[1.02] sm:leading-[0.95] tracking-tight">Le Magazine pédagogique</h2>
        <p className="mt-3 sm:mt-4 text-white/60 text-[13.5px] sm:text-[15px] max-w-3xl">9 numéros pédagogiques pour <strong className="text-white">comprendre</strong> le code de la route, sans chrono, sans score. Chaque numéro = un thème, un récit éditorial à lire à votre rythme.</p>
      </div>

      {/* Featured */}
      <div className="rounded-xl sm:rounded-2xl border border-red-900/40 bg-gradient-to-br from-red-950/30 via-[#160a0a] to-[#0d0d0e] p-5 sm:p-7 lg:p-9 mb-4 sm:mb-6">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-semibold mb-3">{featuredIssue.badge}</div>
        <div className="grid lg:grid-cols-[1fr_auto] gap-4 sm:gap-6 items-end">
          <div>
            <div className="text-white/50 text-[11px] sm:text-[12px] font-mono-spec mb-2">{featuredIssue.number}</div>
            <h3 className="font-serif-editorial text-white text-[22px] sm:text-[28px] lg:text-[40px] leading-[1.05] max-w-3xl">{featuredIssue.title}</h3>
            <p className="text-white/70 text-[13px] sm:text-[14.5px] mt-2 sm:mt-3 max-w-2xl">{featuredIssue.desc}</p>
            <div className="mt-4 sm:mt-5 flex flex-wrap items-center gap-2 text-[11px] sm:text-[12px] text-white/50 font-mono-spec">
              {featuredIssue.meta.map((m, i) => (
                <React.Fragment key={i}>
                  <span>{m}</span>
                  {i < featuredIssue.meta.length - 1 && <span className="w-1 h-1 rounded-full bg-white/30" />}
                </React.Fragment>
              ))}
            </div>
          </div>
          <button className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-red-900/40">
            {featuredIssue.cta} <ArrowRight size={15} />
          </button>
        </div>
      </div>

      {/* 9 issues grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-3.5">
        {magazineIssues.map((m, i) => (
          <Link key={i} to="/magazine" className="group relative rounded-xl border border-white/10 bg-[#111113] p-4 sm:p-5 hover:border-red-700/40 transition-colors">
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <span className="font-mono-spec text-[13px] sm:text-[14px] text-red-500/80">{m.n}</span>
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold inline-flex items-center gap-1">
                <BookOpen size={11} /> Lire
              </span>
            </div>
            <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px] leading-tight">{m.title}</h3>
            <p className="text-white/60 text-[12px] sm:text-[13px] mt-1.5 sm:mt-2 line-clamp-3">{m.desc}</p>
            <ArrowUpRight size={16} className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 text-white/30 group-hover:text-red-400 transition" />
          </Link>
        ))}
      </div>
      <div className="mt-4 sm:mt-5 text-center text-white/50 text-[11.5px] sm:text-[12.5px] font-mono-spec">
        9 numéros publiés sur 9 · Édition pilote disponible immédiatement
      </div>
    </section>
  );
}
